import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  useGetAdminCardCatalogQuery,
  useGetAdminCardScheduleQuery,
  useGetAdminCardScheduleDayQuery,
  useEnsureAdminCardScheduleHorizonMutation,
  useRegenerateAdminCardScheduleMutation,
  usePreviewAdminCardScheduleMutation,
  usePatchAdminCardScheduleMutation,
  type AdminScheduleSummary,
  type AdminScheduleSlot,
  type AdminScheduleAotdBonus,
  type AdminScheduleDecay,
  type AdminSchedulePreview,
} from "../../api/respectCardsApi";
import InfoDot from "../InfoDot";

/**
 * Phase 2b cards-launch — Schedule tab.
 * 14-day list with inline editor + preview simulation.
 */

const card: React.CSSProperties = {
  background: "#fff",
  padding: 16,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  marginBottom: 16,
};

const row: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 12px",
  border: "1px solid #ece6f7",
  borderRadius: 6,
  marginBottom: 8,
  background: "#faf7ff",
};

const tierChip = (rarity: string): React.CSSProperties => ({
  display: "inline-block",
  fontSize: "0.7rem",
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 999,
  marginRight: 6,
  background: "rgba(99, 102, 241, 0.15)",
  color: "#3730a3",
  textTransform: "uppercase",
});

const statusBadge = (status: string): React.CSSProperties => {
  const colors: Record<string, [string, string]> = {
    scheduled: ["#fef3c7", "#92400e"],
    open: ["#dcfce7", "#166534"],
    closed: ["#e0e7ff", "#3730a3"],
  };
  const [bg, fg] = colors[status] ?? ["#e5e7eb", "#374151"];
  return {
    background: bg,
    color: fg,
    fontSize: "0.72rem",
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  };
};

const btn: React.CSSProperties = {
  fontSize: "0.82rem",
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid #c7c1e0",
  background: "#fff",
  color: "#3730a3",
  cursor: "pointer",
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: "#4f46e5",
  borderColor: "#4f46e5",
  color: "#fff",
};

const inputCss: React.CSSProperties = {
  fontSize: "0.85rem",
  padding: "4px 8px",
  borderRadius: 4,
  border: "1px solid #d1d5db",
  width: 90,
};

const labelCss: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: "0.82rem",
  color: "#444",
  marginRight: 12,
};

interface EditorState {
  slots: AdminScheduleSlot[];
  aotdBonus: AdminScheduleAotdBonus;
  decay: AdminScheduleDecay;
}

// datetime-local <-> ISO helpers for the "open time" control. The input works
// in the operator's LOCAL timezone; we convert to a UTC ISO for the API (and
// echo the resulting UTC so there's no ambiguity).
function isoToLocalInput(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}
function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local); // parsed in the browser's local timezone
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const ScheduleTab: React.FC = () => {
  const { data: scheduleList, isLoading, error } = useGetAdminCardScheduleQuery();
  const { data: catalogData } = useGetAdminCardCatalogQuery();
  const [ensureHorizon, ensureState] = useEnsureAdminCardScheduleHorizonMutation();
  const [daysAhead, setDaysAhead] = useState<number>(14);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  const cards = catalogData?.cards ?? [];

  const handleEnsure = async () => {
    try {
      await ensureHorizon({ daysAhead }).unwrap();
      toast.success(`horizon ensured (${daysAhead} days)`);
    } catch (e) {
      toast.error("failed to ensure horizon");
    }
  };

  return (
    <section style={card}>
      <h2 style={{ fontSize: "1.1rem", color: "#333", marginBottom: 12 }}>
        Schedule
      </h2>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <label style={labelCss}>
          days ahead
          <input
            type="number"
            min={1}
            max={60}
            step={1}
            value={daysAhead}
            onChange={(e) => setDaysAhead(Number(e.target.value) || 14)}
            style={inputCss}
          />
        </label>
        <button
          type="button"
          style={btnPrimary}
          onClick={handleEnsure}
          disabled={ensureState.isLoading}
        >
          {ensureState.isLoading ? "ensuring…" : "Ensure horizon"}
        </button>
      </div>

      {isLoading ? (
        <p style={{ color: "#666" }}>loading schedule…</p>
      ) : error ? (
        <p style={{ color: "#a00" }}>failed to load schedule.</p>
      ) : (scheduleList?.schedules ?? []).length === 0 ? (
        <p style={{ color: "#666" }}>no schedules yet — click “Ensure horizon”.</p>
      ) : (
        <div>
          {(scheduleList?.schedules ?? []).map((schedule) => (
            <ScheduleRow
              key={schedule.date}
              schedule={schedule}
              expanded={expandedDate === schedule.date}
              onToggle={() =>
                setExpandedDate((cur) => (cur === schedule.date ? null : schedule.date))
              }
              cardOptions={cards.map((c) => ({
                value: c.slug || c.id,
                label: `${c.emoji ?? "•"} ${c.name} (${c.rarity})`,
              }))}
            />
          ))}
        </div>
      )}
    </section>
  );
};

interface ScheduleRowProps {
  schedule: AdminScheduleSummary;
  expanded: boolean;
  onToggle: () => void;
  cardOptions: { value: string; label: string }[];
}

const ScheduleRow: React.FC<ScheduleRowProps> = ({
  schedule,
  expanded,
  onToggle,
  cardOptions,
}) => {
  return (
    <div>
      <div style={row}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <strong style={{ fontFamily: "monospace" }}>{schedule.date}</strong>
          <span style={statusBadge(schedule.status)}>{schedule.status}</span>
          <span>
            {schedule.slots.map((slot, i) => (
              <span key={i} style={tierChip(slot.rarity)}>
                {slot.rarity}
              </span>
            ))}
          </span>
        </div>
        <button type="button" style={btn} onClick={onToggle}>
          {expanded ? "Close" : "Edit"}
        </button>
      </div>
      {expanded ? (
        <ScheduleEditor schedule={schedule} cardOptions={cardOptions} />
      ) : null}
    </div>
  );
};

interface ScheduleEditorProps {
  schedule: AdminScheduleSummary;
  cardOptions: { value: string; label: string }[];
}

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ schedule, cardOptions }) => {
  const date = schedule.date;
  const { data, isLoading, error } = useGetAdminCardScheduleDayQuery(date);
  const [patchSchedule, patchState] = usePatchAdminCardScheduleMutation();
  const [regenerate, regenState] = useRegenerateAdminCardScheduleMutation();
  const [previewSchedule, previewState] = usePreviewAdminCardScheduleMutation();

  const initial: EditorState | null = useMemo(() => {
    if (!data?.schedule) return null;
    return {
      slots: data.schedule.slots.map((s) => ({ ...s })),
      aotdBonus: { ...data.schedule.aotdBonus },
      decay: { ...data.schedule.decay },
    };
  }, [data]);

  const [state, setState] = useState<EditorState | null>(null);
  const [pullers, setPullers] = useState<number>(10);
  const [aotdHolderIndex, setAotdHolderIndex] = useState<number>(-1);
  const [preview, setPreview] = useState<AdminSchedulePreview | null>(null);
  const [openLocal, setOpenLocal] = useState<string>(() => isoToLocalInput(schedule.opensAt));

  React.useEffect(() => {
    if (initial) setState(initial);
  }, [initial]);

  if (isLoading) {
    return <p style={{ color: "#666", padding: "0 12px" }}>loading day…</p>;
  }
  if (error || !state) {
    return <p style={{ color: "#a00", padding: "0 12px" }}>failed to load day.</p>;
  }

  const updateSlot = (idx: number, patch: Partial<AdminScheduleSlot>) => {
    setState((prev) =>
      prev
        ? {
            ...prev,
            slots: prev.slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
          }
        : prev,
    );
  };

  const handleSave = async () => {
    try {
      await patchSchedule({
        date,
        patch: {
          slots: state.slots,
          aotdBonus: state.aotdBonus,
          decay: state.decay,
        },
      }).unwrap();
      toast.success(`saved patch for ${date}`);
    } catch (e) {
      toast.error("failed to save patch");
    }
  };

  const handleRegenerate = async () => {
    try {
      await regenerate({ date }).unwrap();
      toast.success(`regenerated ${date}`);
    } catch (e) {
      toast.error("failed to regenerate");
    }
  };

  const handleSetOpenTime = async () => {
    const iso = localInputToIso(openLocal);
    if (!iso) {
      toast.error("pick a valid open time");
      return;
    }
    try {
      // Belt-and-suspenders: backend ALSO auto-snaps closesAt to opensAt+1h, but
      // sending it explicit means an older backend deploy (without the auto-snap)
      // still gets the right value. Explicit patch.closesAt always wins server-side.
      const closesIso = new Date(new Date(iso).getTime() + 60 * 60 * 1000).toISOString();
      await patchSchedule({ date, patch: { opensAt: iso, closesAt: closesIso } }).unwrap();
      toast.success(`open time set — ${iso} (closes ${closesIso})`);
    } catch (e) {
      toast.error("failed to set open time");
    }
  };

  const handlePreview = async () => {
    try {
      const result = await previewSchedule({
        date,
        overrides: {
          slots: state.slots,
          aotdBonus: state.aotdBonus,
          decay: state.decay,
          pullers,
          aotdHolderIndex,
        },
      }).unwrap();
      setPreview(result.preview);
    } catch (e) {
      toast.error("failed to run preview");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #ddd6f3",
        borderRadius: 6,
        padding: 12,
        marginBottom: 12,
        background: "#fff",
      }}
    >
      <h3 style={{ fontSize: "0.95rem", color: "#333", marginBottom: 8 }}>
        Editor — {date}
      </h3>

      <div style={{ marginBottom: 12 }}>
        <strong style={{ fontSize: "0.85rem" }}>Slots</strong>
        {state.slots.map((slot, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 8,
              borderBottom: "1px dashed #eee",
              flexWrap: "wrap",
            }}
          >
            <span style={tierChip(slot.rarity)}>{slot.rarity}</span>
            <label style={labelCss}>
              base hit
              <InfoDot fieldKey="schedule.baseHitChance" />
              <input
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={slot.baseHitChance}
                onChange={(e) =>
                  updateSlot(idx, { baseHitChance: Number(e.target.value) })
                }
                style={inputCss}
              />
            </label>
            <label style={labelCss}>
              pinned card
              <InfoDot fieldKey="schedule.pinnedCardId" />
              <select
                value={slot.pinnedCardId ?? ""}
                onChange={(e) =>
                  updateSlot(idx, {
                    pinnedCardId: e.target.value ? e.target.value : null,
                  })
                }
                style={{ ...inputCss, width: 240 }}
              >
                <option value="">— rarity pool —</option>
                {cardOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <strong style={{ fontSize: "0.85rem" }}>AOTD bonus</strong>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: 8 }}>
          <label style={labelCss}>
            <input
              type="checkbox"
              checked={state.aotdBonus.enabled}
              onChange={(e) =>
                setState((prev) =>
                  prev
                    ? { ...prev, aotdBonus: { ...prev.aotdBonus, enabled: e.target.checked } }
                    : prev,
                )
              }
            />
            enabled
            <InfoDot fieldKey="schedule.aotdBonus.enabled" />
          </label>
          <label style={labelCss}>
            pct bonus
            <InfoDot fieldKey="schedule.aotdBonus.pctBonus" />
            <input
              type="number"
              min={0}
              max={0.2}
              step={0.01}
              value={state.aotdBonus.pctBonus}
              onChange={(e) =>
                setState((prev) =>
                  prev
                    ? {
                        ...prev,
                        aotdBonus: {
                          ...prev.aotdBonus,
                          pctBonus: Number(e.target.value),
                        },
                      }
                    : prev,
                )
              }
              style={inputCss}
            />
          </label>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <strong style={{ fontSize: "0.85rem" }}>Decay</strong>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: 8 }}>
          <label style={labelCss}>
            decay/slot
            <InfoDot fieldKey="schedule.decayPerSlot" />
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={state.decay.decayPerSlot}
              onChange={(e) =>
                setState((prev) =>
                  prev
                    ? { ...prev, decay: { ...prev.decay, decayPerSlot: Number(e.target.value) } }
                    : prev,
                )
              }
              style={inputCss}
            />
          </label>
          <label style={labelCss}>
            min mult
            <InfoDot fieldKey="schedule.decay.minMultiplier" />
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={state.decay.minMultiplier}
              onChange={(e) =>
                setState((prev) =>
                  prev
                    ? { ...prev, decay: { ...prev.decay, minMultiplier: Number(e.target.value) } }
                    : prev,
                )
              }
              style={inputCss}
            />
          </label>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <strong style={{ fontSize: "0.85rem" }}>Open time</strong>
        <InfoDot fieldKey="schedule.opensAt" />
        {schedule.status !== "closed" ? (
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: 8 }}>
            <label style={labelCss}>
              opens at (your local time)
              <input
                type="datetime-local"
                value={openLocal}
                onChange={(e) => setOpenLocal(e.target.value)}
                style={{ ...inputCss, width: 210 }}
              />
            </label>
            <span style={{ fontSize: "0.78rem", color: "#666" }}>
              {localInputToIso(openLocal)
                ? `→ ${localInputToIso(openLocal)} (UTC) · closesAt auto-snaps to +1h`
                : "pick a date & time"}
            </span>
            <button
              type="button"
              style={btn}
              onClick={handleSetOpenTime}
              disabled={patchState.isLoading}
            >
              {patchState.isLoading ? "saving…" : "Set open time"}
            </button>
            {schedule.status === "open" ? (
              <span style={{ fontSize: "0.75rem", color: "#92400e" }}>
                ⚠️ this drop is already <code>open</code>; setting a future time reverts it
                to <code>scheduled</code> so the new open fires cleanly. Existing lobby
                state (if any) is preserved.
              </span>
            ) : null}
          </div>
        ) : (
          <p style={{ fontSize: "0.8rem", color: "#888", padding: 8 }}>
            open time is frozen — drop is already <code>closed</code>
            {schedule.opensAt ? ` (was ${schedule.opensAt})` : ""}.
          </p>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          style={btnPrimary}
          onClick={handleSave}
          disabled={patchState.isLoading}
        >
          {patchState.isLoading ? "saving…" : "Save patch"}
        </button>
        {schedule.status === "scheduled" ? (
          <button
            type="button"
            style={btn}
            onClick={handleRegenerate}
            disabled={regenState.isLoading}
          >
            {regenState.isLoading ? "regenerating…" : "Regenerate"}
          </button>
        ) : null}
      </div>

      <div style={{ borderTop: "1px solid #eee", paddingTop: 12 }}>
        <strong style={{ fontSize: "0.95rem", color: "#333" }}>Simulation</strong>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: 8 }}>
          <label style={labelCss}>
            pullers
            <InfoDot fieldKey="schedule.simulation.pullers" />
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              value={pullers}
              onChange={(e) => setPullers(Number(e.target.value) || 1)}
              style={inputCss}
            />
          </label>
          <label style={labelCss}>
            aotd holder index
            <InfoDot fieldKey="schedule.simulation.aotdHolderIndex" />
            <input
              type="number"
              min={-1}
              max={49}
              step={1}
              value={aotdHolderIndex}
              onChange={(e) => setAotdHolderIndex(Number(e.target.value))}
              style={inputCss}
            />
          </label>
          <button
            type="button"
            style={btnPrimary}
            onClick={handlePreview}
            disabled={previewState.isLoading}
          >
            {previewState.isLoading ? "running…" : "Run preview"}
          </button>
        </div>
        {preview ? <PreviewResult preview={preview} /> : null}
      </div>
    </div>
  );
};

const PreviewResult: React.FC<{ preview: AdminSchedulePreview }> = ({ preview }) => {
  const pct = (v: number | null | undefined) =>
    v != null ? (v * 100).toFixed(1) + "%" : "—";
  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          background: "#f5f5fa",
          border: "1px solid #e3e3ef",
          borderRadius: 6,
          padding: 10,
          marginBottom: 12,
          fontSize: "0.85rem",
          color: "#333",
        }}
      >
        <strong style={{ fontSize: "0.9rem" }}>v2 summary</strong>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6 }}>
          <span>
            total supply: <strong>{preview.totalSupply ?? "—"}</strong>
          </span>
          <span>
            expected hit rate: <strong>{pct(preview.expectedHitRate)}</strong>
          </span>
          <span>
            expected nothing rate: <strong>{pct(preview.expectedNothingRate)}</strong>
          </span>
        </div>
        {preview.cardDistribution ? (
          <div style={{ marginTop: 6 }}>
            distribution:{" "}
            {Object.entries(preview.cardDistribution).map(([key, count]) => (
              <span key={key} style={{ marginRight: 8 }}>
                <code>{key}</code> ×{count}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.82rem",
          marginBottom: 12,
        }}
      >
        <thead>
          <tr style={{ background: "#f5f5fa", textAlign: "left" }}>
            <th style={{ padding: 6 }}>rarity</th>
            <th style={{ padding: 6 }}>base</th>
            <th style={{ padding: 6 }}>decay</th>
            <th style={{ padding: 6 }}>effective</th>
            <th style={{ padding: 6 }}>exp. hits</th>
            <th style={{ padding: 6 }}>exp. nothing</th>
          </tr>
        </thead>
        <tbody>
          {preview.slots.map((slot, idx) => (
            <tr key={idx} style={{ borderTop: "1px solid #eee" }}>
              <td style={{ padding: 6 }}>{slot.rarity}</td>
              <td style={{ padding: 6 }}>{slot.baseHitChance?.toFixed(3) ?? "—"}</td>
              <td style={{ padding: 6 }}>{slot.decayMultiplier?.toFixed(3) ?? "—"}</td>
              <td style={{ padding: 6 }}>{slot.effectiveHitChance?.toFixed(3) ?? "—"}</td>
              <td style={{ padding: 6 }}>{slot.expectedHits?.toFixed(2) ?? "—"}</td>
              <td style={{ padding: 6 }}>{slot.expectedNothing?.toFixed(2) ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {preview.perUser && preview.perUser.length > 0 ? (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.82rem",
            marginBottom: 12,
          }}
        >
          <thead>
            <tr style={{ background: "#f5f5fa", textAlign: "left" }}>
              <th style={{ padding: 6 }}>idx</th>
              <th style={{ padding: 6 }}>arrival rank</th>
              <th style={{ padding: 6 }}>weight</th>
              <th style={{ padding: 6 }}>win chance</th>
              <th style={{ padding: 6 }}>outcome</th>
              <th style={{ padding: 6 }}>drawn rarity</th>
            </tr>
          </thead>
          <tbody>
            {preview.perUser.map((u) => (
              <tr key={u.index} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ padding: 6 }}>{u.index}</td>
                <td style={{ padding: 6 }}>{u.arrivalRank}</td>
                <td style={{ padding: 6 }}>{u.weight?.toFixed(3) ?? "—"}</td>
                <td style={{ padding: 6 }}>{pct(u.winChance)}</td>
                <td style={{ padding: 6 }}>{u.outcome}</td>
                <td style={{ padding: 6 }}>{u.drawnRarity ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {preview.poolRemaining && preview.poolRemaining.length > 0 ? (
        <div style={{ fontSize: "0.85rem", color: "#444" }}>
          pool depletion:{" "}
          {preview.poolRemaining.map((p, i) => (
            <span key={i} style={{ marginRight: 10 }}>
              <code>{p.rarity}</code> {p.total} → {p.remaining}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ScheduleTab;
